<!-- Kiosk / screen shell (doc 34, F1). Runs on a fixed display with NO user
     session: it authenticates with a device token (enrolled once via a code)
     and renders the manifest the gateway returns — a list of dashboard slides
     it rotates. The device token never reaches the slide iframes; each slide
     loads its own per-dashboard embed JWT minted server-side. -->
<template>
  <div class="kiosk" :class="`o-${orientation}`">
    <!-- Enrollment (no token yet) -->
    <div v-if="phase === 'enroll'" class="center">
      <div class="card">
        <h1>Lymon screen</h1>
        <p>Enter the enrollment code shown in the portal.</p>
        <input v-model="codeInput" class="code" placeholder="ldve_…" @keyup.enter="enroll" />
        <button :disabled="!codeInput.trim() || busy" @click="enroll">{{ busy ? 'Enrolling…' : 'Enroll' }}</button>
        <p v-if="error" class="err">{{ error }}</p>
      </div>
    </div>

    <!-- Loading / error -->
    <div v-else-if="phase === 'loading'" class="center"><p class="muted">{{ offline ? 'Reconnecting…' : 'Loading…' }}</p></div>
    <div v-else-if="phase === 'error'" class="center">
      <div class="card"><p class="err">{{ error }}</p><button @click="reset">Re-enroll</button></div>
    </div>

    <!-- Paused: the screen is disabled in the portal. Keep the enrollment; it
         resumes automatically when re-enabled (push, or the next poll). -->
    <div v-else-if="paused" class="center">
      <div class="card"><h1>{{ deviceName || 'Screen' }}</h1><p>This screen is paused.</p><p class="muted small">It will resume automatically when re-enabled.</p></div>
    </div>

    <div v-else-if="!slides.length" class="center">
      <div class="card"><p class="muted">No content assigned yet.</p><p class="muted small">Assign a dashboard, board or playlist to this screen in the portal.</p></div>
    </div>

    <!-- Blanking window (display off by schedule) -->
    <div v-else-if="blanked" class="blank"></div>

    <!-- Running carousel -->
    <template v-else>
      <iframe
        v-for="(s, i) in slides"
        v-show="i === idx"
        :key="s.id + ':' + i"
        :src="s.url"
        class="slide"
        frameborder="0"
      ></iframe>
    </template>

    <!-- Identify: full-screen flash so an operator can find this physical screen -->
    <div v-if="identifying" class="identify"><span>{{ deviceName || 'This screen' }}</span></div>

    <!-- Offline indicator: lost contact with the gateway; showing last content. -->
    <div v-if="offline && phase === 'running'" class="offline" title="Reconnecting…">⚠ offline</div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:3013';
const TOKEN_KEY = 'lymon_device_token';
const MANIFEST_CACHE = 'lymon_device_manifest';

interface Slide { kind: string; id: string; title: string; url: string; dwellS?: number }
interface Manifest {
  ok?: boolean;
  paused?: boolean;
  device?: { id: string; name: string };
  config?: { rotateS?: number; orientation?: string; theme?: string; blanking?: { from: string; to: string } };
  slides?: Slide[];
  skipped?: number;
  refresh_s?: number;
}

const token = ref<string>(localStorage.getItem(TOKEN_KEY) ?? '');
const phase = ref<'enroll' | 'loading' | 'running' | 'error'>(token.value ? 'loading' : 'enroll');
const codeInput = ref('');
const busy = ref(false);
const error = ref<string | null>(null);
const deviceName = ref('');
const identifying = ref(false);
const paused = ref(false);
const offline = ref(false);

const slides = ref<Slide[]>([]);
const idx = ref(0);
const config = ref<Manifest['config']>({});
const orientation = computed(() => (config.value?.orientation === 'portrait' ? 'portrait' : 'landscape'));

// Blanking schedule ("HH:MM"–"HH:MM"; wraps past midnight).
const now = ref(Date.now());
const blanked = computed(() => {
  const b = config.value?.blanking;
  if (!b?.from || !b?.to) return false;
  const d = new Date(now.value);
  const cur = d.getHours() * 60 + d.getMinutes();
  const [fh, fm] = b.from.split(':').map(Number);
  const [th, tm] = b.to.split(':').map(Number);
  const from = (fh || 0) * 60 + (fm || 0);
  const to = (th || 0) * 60 + (tm || 0);
  return from <= to ? cur >= from && cur < to : cur >= from || cur < to;
});

let rotateTimer: ReturnType<typeof setTimeout> | null = null;
let manifestTimer: ReturnType<typeof setInterval> | null = null;
let beatTimer: ReturnType<typeof setInterval> | null = null;
let clockTimer: ReturnType<typeof setInterval> | null = null;
let identifyTimer: ReturnType<typeof setTimeout> | null = null;
// Control channel (push). The socket keeps itself alive with heartbeats; on
// drop it reconnects with capped backoff. Manifest poll covers any gap.
let ws: WebSocket | null = null;
let wsRetry = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const APP_VERSION = '1';

// The WS control channel pushes "reload" the instant the portal changes a
// screen, so the manifest poll is only a FALLBACK (when the socket is down on a
// flaky plant network) — a slower interval is enough.
const POLL_MS = 60_000;
// Slide tokens live 3600s; force-refresh them well before expiry even when the
// content is unchanged (the only time we reload otherwise-static iframes).
const TOKEN_REFRESH_MS = 50 * 60_000;
let contentSig = '';
let lastTokenMs = 0;

function headers(): HeadersInit { return { Authorization: `Bearer ${token.value}`, 'Content-Type': 'application/json' }; }

// Strip the one-time ?enroll=<code> from the URL once we hold a token, so a
// later refresh never re-submits the (now consumed) code → "already used".
function clearEnrollParam(): void {
  try {
    const base = window.location.href.split('#')[0];
    window.history.replaceState(null, '', `${base}#/kiosk`);
  } catch { /* ignore */ }
}

async function enroll(): Promise<void> {
  const code = codeInput.value.trim();
  if (!code) return;
  busy.value = true; error.value = null;
  try {
    const res = await fetch(`${API_BASE}/api/device/enroll`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }) });
    const data = await res.json();
    if (!res.ok || !data.token) { error.value = data.detail || data.error || 'Enrollment failed'; return; }
    token.value = data.token;
    localStorage.setItem(TOKEN_KEY, data.token);
    clearEnrollParam(); // spent — don't let a refresh retry it
    phase.value = 'loading';
    await start();
  } catch (e) { error.value = e instanceof Error ? e.message : String(e); }
  finally { busy.value = false; }
}

function applyTheme(t?: string): void {
  const dark = t === 'dark' || (t === 'auto' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', !!dark);
}

async function loadManifest(): Promise<void> {
  // Pass our own public origin so the gateway builds slide URLs against it
  // (tunnel / reverse-proxy) instead of its localhost dev defaults.
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/device/manifest?origin=${encodeURIComponent(window.location.origin)}`, { headers: headers() });
  } catch (e) {
    // Network/gateway unreachable: flag offline, keep whatever's on screen.
    offline.value = true;
    throw e;
  }
  offline.value = false;
  // 401 = the token is genuinely invalid (revoked/deleted) → re-enroll. A
  // *disabled* device still authenticates and returns paused:true (handled
  // below), so it never resets and keeps its enrollment.
  if (res.status === 401) { reset(); throw new Error('unauthorized'); }
  const data: Manifest = await res.json();
  try { localStorage.setItem(MANIFEST_CACHE, JSON.stringify(data)); } catch { /* quota */ }
  applyManifest(data);
}

// Apply a manifest (live or cached) to the view.
function applyManifest(data: Manifest): void {
  applyTheme(data.config?.theme);
  if (data.paused) {
    // Paused in the portal: show the inactive screen, keep token + timers so it
    // resumes on the next reload push / poll. Don't touch the running slides.
    paused.value = true;
    config.value = data.config ?? {};
    phase.value = 'running';
    return;
  }
  paused.value = false;
  const nextConfig = data.config ?? {};
  const next = data.slides ?? [];
  // Signature of everything that affects what's on screen (NOT the per-poll
  // slide tokens). If unchanged we leave the running iframes alone, so a 15s
  // poll never causes a flicker — we only swap when the content really changed.
  const sig = JSON.stringify([
    next.map((s) => [s.id, s.dwellS ?? null]),
    nextConfig.rotateS ?? null, nextConfig.orientation ?? null,
    nextConfig.theme ?? null, nextConfig.blanking ?? null,
  ]);
  const first = phase.value !== 'running';
  const tokenStale = Date.now() - lastTokenMs > TOKEN_REFRESH_MS;
  if (sig === contentSig && !first && !tokenStale) return;

  const contentChanged = sig !== contentSig;
  contentSig = sig;
  lastTokenMs = Date.now();
  config.value = nextConfig;
  applyTheme(nextConfig.theme);
  slides.value = next;
  if (idx.value >= next.length) idx.value = 0;
  phase.value = 'running';
  // Re-time rotation only when content changed (or first load); a pure token
  // refresh keeps the current slide and its remaining dwell.
  if (contentChanged || first) scheduleRotate();
}

function scheduleRotate(): void {
  if (rotateTimer) clearTimeout(rotateTimer);
  if (slides.value.length <= 1) return;
  const cur = slides.value[idx.value];
  const dwell = (cur?.dwellS ?? config.value?.rotateS ?? 20) * 1000;
  rotateTimer = setTimeout(() => {
    idx.value = (idx.value + 1) % slides.value.length;
    void heartbeat();
    scheduleRotate();
  }, dwell);
}

function currentStatus(): { kind: string; id: string; title?: string } | undefined {
  const c = slides.value[idx.value];
  return c ? { kind: c.kind, id: c.id, title: c.title } : undefined;
}

// Report liveness + what's on screen. Prefer the WS (also keeps the socket
// alive past its idle window); fall back to HTTP when the socket is down.
async function heartbeat(): Promise<void> {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ kind: 'heartbeat', current: currentStatus() }));
    return;
  }
  try {
    await fetch(`${API_BASE}/api/device/heartbeat`, {
      method: 'POST', headers: headers(), body: JSON.stringify({ current: currentStatus() }),
    });
  } catch { /* heartbeat is best-effort */ }
}

// ── Control channel (push) ────────────────────────────────────────────
function wsUrl(): string { return `${API_BASE.replace(/^http/, 'ws')}/api/device-control`; }
function connectWs(): void {
  if (!token.value || ws) return;
  let sock: WebSocket;
  try { sock = new WebSocket(wsUrl()); } catch { scheduleReconnect(); return; }
  ws = sock;
  sock.onopen = () => { wsRetry = 0; sock.send(JSON.stringify({ kind: 'hello', token: token.value, app_version: APP_VERSION, current: currentStatus() })); };
  sock.onmessage = (ev) => handleWsMessage(String(ev.data));
  sock.onerror = () => { try { sock.close(); } catch { /* ignore */ } };
  sock.onclose = () => { if (ws === sock) ws = null; scheduleReconnect(); };
}
function scheduleReconnect(): void {
  if (!token.value || reconnectTimer) return;
  const delay = Math.min(30_000, 1000 * 2 ** Math.min(wsRetry++, 5));
  reconnectTimer = setTimeout(() => { reconnectTimer = null; connectWs(); }, delay);
}
function disconnectWs(): void {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  const sock = ws; ws = null;
  try { sock?.close(); } catch { /* ignore */ }
}
function handleWsMessage(raw: string): void {
  let m: { kind?: string; ok?: boolean; command?: string; duration_s?: number; name?: string };
  try { m = JSON.parse(raw); } catch { return; }
  if (m.kind === 'hello_ack') {
    if (m.ok === false) { reset(); return; }
    if (m.name) deviceName.value = m.name;
  } else if (m.kind === 'command') {
    if (m.command === 'reload') void loadManifest().catch(() => {});
    else if (m.command === 'refresh') window.location.reload();
    else if (m.command === 'identify') flashIdentify(m.duration_s ?? 10);
  }
}
function flashIdentify(s: number): void {
  identifying.value = true;
  if (identifyTimer) clearTimeout(identifyTimer);
  identifyTimer = setTimeout(() => { identifying.value = false; }, s * 1000);
}

// Cold-start fallback: render the last manifest we saw (best-effort — slide
// tokens may have expired, but it beats a blank screen while reconnecting).
function applyCachedManifest(): void {
  try {
    const raw = localStorage.getItem(MANIFEST_CACHE);
    if (raw) applyManifest(JSON.parse(raw) as Manifest);
  } catch { /* ignore */ }
}

async function start(): Promise<void> {
  try {
    await loadManifest();
  } catch (e) {
    // Offline at cold start → show cached content; a real error (and a token
    // still present) → error screen. reset() (401) clears the token: bail.
    if (offline.value) applyCachedManifest();
    else if (token.value && phase.value !== 'running') { phase.value = 'error'; error.value = e instanceof Error ? e.message : String(e); }
  }
  if (!token.value) return; // reset() happened (invalid token)
  connectWs();
  void heartbeat();
  // Always set up the timers so a screen that started offline recovers on its own.
  manifestTimer = setInterval(() => { void loadManifest().catch(() => {}); }, POLL_MS);
  beatTimer = setInterval(() => { void heartbeat(); }, 30_000); // also keeps the WS alive
}

function reset(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(MANIFEST_CACHE);
  token.value = '';
  slides.value = [];
  phase.value = 'enroll';
  paused.value = false;
  offline.value = false;
  contentSig = '';
  if (rotateTimer) clearTimeout(rotateTimer);
  if (manifestTimer) clearInterval(manifestTimer);
  if (beatTimer) clearInterval(beatTimer);
  disconnectWs();
}

onMounted(() => {
  clockTimer = setInterval(() => { now.value = Date.now(); }, 30_000);
  // Auto-enroll if the kiosk URL carries ?enroll=<code> (from the portal).
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? window.location.search);
  const auto = params.get('enroll');
  // Already enrolled (token persisted): ignore any leftover ?enroll in the URL,
  // clean it, and just start — refreshing must never re-submit a spent code.
  if (token.value) { if (auto) clearEnrollParam(); void start(); return; }
  if (auto) { codeInput.value = auto; void enroll(); }
});
onBeforeUnmount(() => {
  if (rotateTimer) clearTimeout(rotateTimer);
  if (manifestTimer) clearInterval(manifestTimer);
  if (beatTimer) clearInterval(beatTimer);
  if (clockTimer) clearInterval(clockTimer);
  if (identifyTimer) clearTimeout(identifyTimer);
  disconnectWs();
});
</script>

<style scoped>
.kiosk { position: fixed; inset: 0; background: #000; overflow: hidden; }
.o-portrait { /* hint for CSS; physical rotation is set on the display itself */ }
.slide { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.blank { position: fixed; inset: 0; background: #000; }
.center { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; font-family: system-ui, sans-serif; color: #e5e7eb; }
.card { background: #111827; border: 1px solid #1f2937; border-radius: 16px; padding: 2rem; max-width: 22rem; text-align: center; }
.card h1 { margin: 0 0 0.5rem; font-size: 1.25rem; }
.card p { margin: 0.25rem 0; color: #9ca3af; font-size: 0.9rem; }
.card p.small { font-size: 0.8rem; }
.code { margin-top: 1rem; width: 100%; padding: 0.6rem 0.75rem; border-radius: 8px; border: 1px solid #374151; background: #0b1220; color: #e5e7eb; font-family: ui-monospace, monospace; box-sizing: border-box; }
.card button { margin-top: 0.75rem; width: 100%; padding: 0.6rem; border: 0; border-radius: 8px; background: #2563eb; color: #fff; font-weight: 600; cursor: pointer; }
.card button:disabled { opacity: 0.5; cursor: default; }
.muted { color: #9ca3af; }
.err { color: #f87171; margin-top: 0.75rem; font-size: 0.85rem; }
.offline { position: fixed; right: 10px; bottom: 10px; z-index: 20; padding: 4px 10px; border-radius: 999px; background: rgba(180, 83, 9, 0.92); color: #fff; font: 600 0.75rem system-ui, sans-serif; }
.identify { position: fixed; inset: 0; z-index: 10; display: flex; align-items: center; justify-content: center; color: #fff; font: 700 3rem/1.2 system-ui, sans-serif; animation: id-flash 0.8s ease-in-out infinite; }
@keyframes id-flash { 0%, 100% { background: rgba(37, 99, 235, 0.92); } 50% { background: rgba(37, 99, 235, 0.25); } }
</style>
